import React, { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import { sanitizeHTML } from '../../utils/sanitizeHTML';

type AllowedTags = ('strong' | 'em' | 'u' | 'ul' | 'ol' | 'li' | 'p' | 'br' | 'span' | 'a' | 'img')[];

const MAX_INLINE_IMAGE_BYTES = 5 * 1024 * 1024; // 5 MB
const MAX_INLINE_IMAGE_LABEL = '5 MB';

const getSanitizeConfig = (allowedTags: AllowedTags, allowedAttributes: string[]) => {
  if (allowedTags.length === 0) return {};

  const attrs = new Set(allowedAttributes);
  // Alignment is stored as style="text-align: ..." on p/li elements
  attrs.add('style');
  if (allowedTags.includes('a')) {
    attrs.add('href');
    attrs.add('target');
    attrs.add('rel');
  }
  if (allowedTags.includes('img')) {
    attrs.add('src');
    attrs.add('alt');
  }

  return {
    ALLOWED_TAGS: allowedTags,
    ALLOWED_ATTR: [...attrs],
  };
};

const normalizeLinkUrl = (url: string): string => {
  const trimmed = url.trim();
  if (/^(https?:\/\/|mailto:|tel:|#|\/)/i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
};

const keepAllowedAttributes = (root: DocumentFragment, allowedAttributes: Set<string>) => {
  root.querySelectorAll('*').forEach(el => {
    [...el.attributes].forEach(attr => {
      if (!allowedAttributes.has(attr.name.toLowerCase())) {
        el.removeAttribute(attr.name);
      }
    });
  });
};

const isValidLinkUrl = (url: string): boolean => {
  if (/^(mailto:|tel:|#|\/)/i.test(url)) return true;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

const findAnchorInRange = (range: Range, root: HTMLElement): HTMLAnchorElement | null => {
  let node: Node | null = range.commonAncestorContainer;
  while (node && node !== root) {
    if (node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).tagName === 'A') {
      return node as HTMLAnchorElement;
    }
    node = node.parentNode;
  }
  return null;
};

// text-align only works on block elements; we apply it to <p> / <li> instead of wrapping in <div>
const ALIGNABLE_BLOCK_TAGS = new Set(['P', 'LI']);

const TEXT_ALIGN_BY_COMMAND = {
  justifyLeft: 'left',
  justifyCenter: 'center',
  justifyRight: 'right',
} as const;

// Walk up from a node to find the nearest <p> or <li> inside the editor.
const findAlignableBlock = (node: Node, root: HTMLElement): HTMLElement | null => {
  let current: Node | null = node;
  while (current && current !== root) {
    if (current.nodeType === Node.ELEMENT_NODE) {
      const element = current as HTMLElement;
      if (ALIGNABLE_BLOCK_TAGS.has(element.tagName)) {
        return element;
      }
    }
    current = current.parentNode;
  }
  return null;
};

// Collect every <p>/<li> that intersects the current selection (may span multiple blocks).
const collectAlignableBlocks = (range: Range, root: HTMLElement): HTMLElement[] => {
  const blocks: HTMLElement[] = [];
  const seen = new Set<HTMLElement>();

  const add = (element: HTMLElement | null) => {
    if (element && root.contains(element) && !seen.has(element)) {
      seen.add(element);
      blocks.push(element);
    }
  };

  add(findAlignableBlock(range.startContainer, root));
  add(findAlignableBlock(range.endContainer, root));

  const container =
    range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
      ? (range.commonAncestorContainer as HTMLElement)
      : range.commonAncestorContainer.parentElement;

  if (container && root.contains(container)) {
    if (ALIGNABLE_BLOCK_TAGS.has(container.tagName)) {
      add(container);
    }
    for (const element of Array.from(container.querySelectorAll('p, li'))) {
      if (range.intersectsNode(element)) {
        add(element as HTMLElement);
      }
    }
  }

  return blocks;
};

const isInlineOrTextNode = (node: Node): boolean => {
  if (node.nodeType === Node.TEXT_NODE) return true;
  if (node.nodeType !== Node.ELEMENT_NODE) return true;
  const tag = (node as HTMLElement).tagName;
  return !ALIGNABLE_BLOCK_TAGS.has(tag) && tag !== 'UL' && tag !== 'OL';
};

// Returns blocks to align
const ensureAlignableBlocks = (range: Range, root: HTMLElement): HTMLElement[] => {
  const existingBlocks = collectAlignableBlocks(range, root);
  if (existingBlocks.length > 0) return existingBlocks;

  const paragraph = document.createElement('p');

  // Non-empty selection: wrap the selected content in a paragraph
  if (!range.collapsed) {
    try {
      range.surroundContents(paragraph);
    } catch {
      // surroundContents fails when the range partially covers an element
      const contents = range.extractContents();
      paragraph.appendChild(contents);
      range.insertNode(paragraph);
    }
    return [paragraph];
  }

  // Caret in loose inline/text content with no paragraphs yet — wrap the whole editor
  const children = Array.from(root.childNodes);
  if (children.length > 0 && children.every(isInlineOrTextNode)) {
    while (root.firstChild) {
      paragraph.appendChild(root.firstChild);
    }
    root.appendChild(paragraph);
    return [paragraph];
  }

  // Empty editor or caret with nothing to wrap — insert a placeholder paragraph
  paragraph.appendChild(document.createElement('br'));
  range.insertNode(paragraph);
  return [paragraph];
};

const applyTextAlign = (
  align: 'left' | 'center' | 'right',
  inputRef: React.RefObject<HTMLDivElement>,
  onChange: (newValue: string) => void,
  isInternalChangeRef: React.MutableRefObject<boolean>
) => {
  if (!inputRef.current) return;

  inputRef.current.focus();
  const selection = window.getSelection();
  if (!selection?.rangeCount) return;

  const range = selection.getRangeAt(0);
  if (!inputRef.current.contains(range.commonAncestorContainer)) return;

  const blocks = ensureAlignableBlocks(range, inputRef.current);
  for (const block of blocks) {
    if (align === 'left') {
      block.style.removeProperty('text-align');
      if (!block.getAttribute('style')?.trim()) {
        block.removeAttribute('style');
      }
    } else {
      block.style.textAlign = align;
    }
  }

  isInternalChangeRef.current = true;
  onChange(inputRef.current.innerHTML);
};

type UseRichTextEditorOptions = {
  value: string;
  onChange: (newValue: string) => void;
  allowedTags?: AllowedTags;
  allowedAttributes?: string[];
};

const formatText = (
  command:
    | 'bold'
    | 'italic'
    | 'underline'
    | 'insertUnorderedList'
    | 'insertOrderedList'
    | 'justifyLeft'
    | 'justifyCenter'
    | 'justifyRight',
  inputRef: React.RefObject<HTMLDivElement>,
  onChange: (newValue: string) => void,
  isInternalChangeRef: React.MutableRefObject<boolean>
) => {
  if (!inputRef.current) return;

  const selection = window.getSelection();
  if (!selection?.rangeCount) return;

  if (command === 'justifyLeft' || command === 'justifyCenter' || command === 'justifyRight') {
    applyTextAlign(TEXT_ALIGN_BY_COMMAND[command], inputRef, onChange, isInternalChangeRef);
    return;
  }

  const range = selection.getRangeAt(0);
  const tagName = {
    bold: 'strong',
    italic: 'em',
    underline: 'u',
    insertUnorderedList: 'ul',
    insertOrderedList: 'ol',
  }[command];

  if (command === 'insertUnorderedList' || command === 'insertOrderedList') {
    const list = document.createElement(command === 'insertUnorderedList' ? 'ul' : 'ol');
    const li = document.createElement('li');
    li.appendChild(range.extractContents());
    list.appendChild(li);
    range.insertNode(list);
    isInternalChangeRef.current = true;
    onChange((inputRef.current as HTMLDivElement).innerHTML);
    return;
  }

  const isAlreadyFormatted = () => {
    const selectedNodes: Node[] = [];
    const walker = document.createTreeWalker(range.commonAncestorContainer, NodeFilter.SHOW_ALL, {
      acceptNode: node => {
        if (range.intersectsNode(node)) {
          return NodeFilter.FILTER_ACCEPT;
        }
        return NodeFilter.FILTER_REJECT;
      },
    });

    let node = walker.nextNode();
    while (node) {
      selectedNodes.push(node);
      node = walker.nextNode();
    }

    for (const selectedNode of selectedNodes) {
      let currentNode: Node | null = selectedNode;
      while (currentNode && currentNode !== inputRef.current) {
        if (currentNode.nodeType === Node.ELEMENT_NODE) {
          const element = currentNode as HTMLElement;
          if (element.tagName.toLowerCase() === tagName) {
            return element;
          }
        }
        currentNode = currentNode.parentNode;
      }
    }

    const checkNode = (node: Node): HTMLElement | null => {
      let current: Node | null = node;
      while (current && current !== inputRef.current) {
        if (current.nodeType === Node.ELEMENT_NODE) {
          const element = current as HTMLElement;
          if (element.tagName.toLowerCase() === tagName) {
            return element;
          }
        }
        current = current.parentNode;
      }
      return null;
    };

    return checkNode(range.startContainer) || checkNode(range.endContainer);
  };

  const existingTag = isAlreadyFormatted();

  if (existingTag) {
    const fragment = document.createDocumentFragment();
    while (existingTag.firstChild) {
      fragment.appendChild(existingTag.firstChild);
    }
    existingTag.parentNode?.replaceChild(fragment, existingTag);

    const newRange = document.createRange();
    newRange.selectNodeContents(fragment);
    selection.removeAllRanges();
    selection.addRange(newRange);
  } else {
    try {
      const newElement = document.createElement(tagName);
      range.surroundContents(newElement);
    } catch (e: unknown) {
      console.error('Error formatting text', e);
      const contents = range.extractContents();
      const newElement = document.createElement(tagName);
      newElement.appendChild(contents);
      range.insertNode(newElement);
    }
  }

  isInternalChangeRef.current = true;
  onChange((inputRef.current as HTMLDivElement).innerHTML);
};

const insertLink = (
  inputRef: React.RefObject<HTMLDivElement>,
  onChange: (newValue: string) => void,
  isInternalChangeRef: React.MutableRefObject<boolean>
) => {
  if (!inputRef.current) return;

  inputRef.current.focus();
  const selection = window.getSelection();
  if (!selection?.rangeCount) return;

  const range = selection.getRangeAt(0);
  if (!inputRef.current.contains(range.commonAncestorContainer)) return;

  const existingAnchor = findAnchorInRange(range, inputRef.current);
  const defaultUrl = existingAnchor?.getAttribute('href') || 'https://';
  const urlInput = window.prompt('Please enter a URL', defaultUrl);

  if (urlInput === null) return;

  const trimmedUrl = urlInput.trim();
  if (!trimmedUrl || trimmedUrl === 'https://') {
    if (existingAnchor) {
      document.execCommand('unlink', false);
      isInternalChangeRef.current = true;
      onChange((inputRef.current as HTMLDivElement).innerHTML);
    }
    return;
  }

  const normalizedUrl = normalizeLinkUrl(trimmedUrl);
  if (!isValidLinkUrl(normalizedUrl)) return;

  if (existingAnchor) {
    existingAnchor.setAttribute('href', normalizedUrl);
    existingAnchor.setAttribute('target', '_blank');
    existingAnchor.setAttribute('rel', 'noopener noreferrer');
  } else {
    document.execCommand('createLink', false, normalizedUrl);
    const anchor = findAnchorInRange(selection.getRangeAt(0), inputRef.current);
    if (anchor) {
      anchor.setAttribute('target', '_blank');
      anchor.setAttribute('rel', 'noopener noreferrer');
    }
  }

  isInternalChangeRef.current = true;
  onChange((inputRef.current as HTMLDivElement).innerHTML);
};

const getOversizedImageError = (oversizedCount: number): string =>
  oversizedCount === 1
    ? `Image is too large. Maximum size is ${MAX_INLINE_IMAGE_LABEL}.`
    : `${oversizedCount} images are too large. Maximum size is ${MAX_INLINE_IMAGE_LABEL}.`;

/** Inserts allowed images; returns an error message if any files were oversized. */
const insertImageFilesAtSelection = (
  imageFiles: File[],
  inputRef: React.RefObject<HTMLDivElement>,
  onChange: (newValue: string) => void
): string | null => {
  if (imageFiles.length === 0 || !inputRef.current) return null;

  const oversizedFiles = imageFiles.filter(file => file.size > MAX_INLINE_IMAGE_BYTES);
  const allowedFiles = imageFiles.filter(file => file.size <= MAX_INLINE_IMAGE_BYTES);
  const errorMessage = oversizedFiles.length > 0 ? getOversizedImageError(oversizedFiles.length) : null;

  if (allowedFiles.length === 0) return errorMessage;

  let currentRange = getInsertRange(inputRef);
  let imageIndex = 0;

  const processNextImage = () => {
    if (imageIndex >= allowedFiles.length) return;
    if (!inputRef.current) return;

    const file = allowedFiles[imageIndex];
    imageIndex++;

    const reader = new FileReader();
    reader.onload = (event: ProgressEvent<FileReader>) => {
      if (!event.target?.result || typeof event.target.result !== 'string') {
        processNextImage();
        return;
      }
      if (!inputRef.current) return;

      const img = document.createElement('img');
      img.src = event.target.result;

      if (!inputRef.current.contains(currentRange.commonAncestorContainer)) {
        currentRange = document.createRange();
        currentRange.selectNodeContents(inputRef.current);
        currentRange.collapse(false);
      }

      currentRange.insertNode(img);
      currentRange = document.createRange();
      currentRange.setStartAfter(img);
      currentRange.collapse(true);

      const newSelection = window.getSelection();
      if (newSelection) {
        newSelection.removeAllRanges();
        newSelection.addRange(currentRange);
      }

      const newValue = (inputRef.current as HTMLDivElement).innerHTML;
      onChange(newValue);
      processNextImage();
    };
    reader.readAsDataURL(file);
  };

  processNextImage();
  return errorMessage;
};

const dataUrlToFile = (dataUrl: string): File | null => {
  if (!dataUrl.startsWith('data:')) return null;
  try {
    const [header, base64] = dataUrl.split(',');
    const mimeMatch = header.match(/data:([^;]+);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/png';
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: mime });
    return new File([blob], 'image', { type: mime });
  } catch {
    return null;
  }
};

const getSelectionRangeInEditor = (inputRef: React.RefObject<HTMLDivElement>): Range | null => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || !inputRef.current) return null;
  const range = selection.getRangeAt(0);
  if (!inputRef.current.contains(range.commonAncestorContainer)) return null;
  return range;
};

/** Return a range for insertion: current selection if in editor, otherwise end of editor. */
const getInsertRange = (inputRef: React.RefObject<HTMLDivElement>): Range => {
  const selectionRange = getSelectionRangeInEditor(inputRef);
  if (selectionRange) return selectionRange;
  const range = document.createRange();
  if (!inputRef.current) return range;
  range.selectNodeContents(inputRef.current);
  range.collapse(false);
  return range;
};

const getDropRange = (x: number, y: number, editor: HTMLDivElement): Range | null => {
  const caretDocument = document as Document & {
    caretRangeFromPoint?: (clientX: number, clientY: number) => Range | null;
    caretPositionFromPoint?: (clientX: number, clientY: number) => { offsetNode: Node; offset: number } | null;
  };

  let range = caretDocument.caretRangeFromPoint?.(x, y) ?? null;
  if (!range) {
    const position = caretDocument.caretPositionFromPoint?.(x, y);
    if (position) {
      range = document.createRange();
      range.setStart(position.offsetNode, position.offset);
      range.collapse(true);
    }
  }

  return range && editor.contains(range.commonAncestorContainer) ? range : null;
};

export const useRichTextEditor = ({
  value,
  onChange,
  allowedTags = [],
  allowedAttributes = [],
}: UseRichTextEditorOptions) => {
  const inputRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const imageInsertRangeRef = useRef<Range | null>(null);
  const draggedImageRef = useRef<HTMLImageElement | null>(null);
  const isInternalChangeRef = useRef(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const hasRichTextTags = allowedTags.some(tag => ['strong', 'em', 'u', 'ul', 'ol', 'li', 'a', 'img'].includes(tag));
  const sanitizeConfig = useMemo(
    () => getSanitizeConfig(allowedTags, allowedAttributes),
    [allowedTags, allowedAttributes]
  );

  // Sync value to innerHTML when value prop changes (only from external sources)
  useEffect(() => {
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return;
    }

    if (inputRef.current) {
      const sanitizedValue = sanitizeHTML(value, sanitizeConfig);
      if (inputRef.current.innerHTML !== sanitizedValue) {
        // Save cursor position before updating
        const selection = window.getSelection();
        let savedRange: Range | null = null;

        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          if (inputRef.current.contains(range.commonAncestorContainer)) {
            // Save the range
            savedRange = range.cloneRange();
          }
        }

        // Update the content
        inputRef.current.innerHTML = sanitizedValue;

        // Restore cursor position if we had one
        if (savedRange) {
          try {
            // Try to restore the exact position
            const newRange = document.createRange();
            const startContainer = savedRange.startContainer;
            const startOffset = savedRange.startOffset;

            // Check if the saved container still exists in the new DOM
            if (inputRef.current.contains(startContainer) && startContainer.nodeType === Node.TEXT_NODE) {
              newRange.setStart(startContainer, Math.min(startOffset, startContainer.textContent?.length || 0));
              newRange.setEnd(startContainer, Math.min(startOffset, startContainer.textContent?.length || 0));
            } else {
              // Fallback: place cursor at end
              newRange.selectNodeContents(inputRef.current);
              newRange.collapse(false);
            }

            if (selection) {
              selection.removeAllRanges();
              selection.addRange(newRange);
            }
          } catch {
            // If cursor restoration fails, just place at end
            const endRange = document.createRange();
            endRange.selectNodeContents(inputRef.current);
            endRange.collapse(false);
            if (selection) {
              selection.removeAllRanges();
              selection.addRange(endRange);
            }
          }
        }
      }
    }
  }, [value, sanitizeConfig]);

  const handleInput = useCallback(
    (e: React.FormEvent<HTMLDivElement>) => {
      const newValue = e.currentTarget.innerHTML;
      isInternalChangeRef.current = true;
      onChange(newValue);
    },
    [onChange]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      e.preventDefault();

      const clipboardData = e.clipboardData;
      const files = Array.from(clipboardData.files ?? []);
      const imageFiles = files.filter(file => file.type.startsWith('image/'));

      if (imageFiles.length > 0) {
        inputRef.current?.focus();
        isInternalChangeRef.current = true;
        setErrorMessage(
          insertImageFilesAtSelection(imageFiles, inputRef, newValue => {
            isInternalChangeRef.current = true;
            onChange(newValue);
          })
        );
        return;
      }

      const range = getInsertRange(inputRef);
      let fragment: DocumentFragment;

      if (clipboardData.types.includes('text/html')) {
        const pastedHtml = clipboardData.getData('text/html');
        if (!pastedHtml) return;

        const pasteAllowedTags = [...allowedTags, 'img'];
        const pasteAllowedAttributes = new Set([...allowedAttributes, 'src', 'alt'].map(attr => attr.toLowerCase()));
        if (allowedTags.includes('a')) {
          pasteAllowedAttributes.add('href');
          pasteAllowedAttributes.add('target');
          pasteAllowedAttributes.add('rel');
        }
        const sanitizedHTML = sanitizeHTML(pastedHtml, {
          ALLOWED_TAGS: pasteAllowedTags,
          ALLOWED_ATTR: [...pasteAllowedAttributes],
        });
        fragment = document.createRange().createContextualFragment(sanitizedHTML);
        keepAllowedAttributes(fragment, pasteAllowedAttributes);
      } else {
        const pastedText = clipboardData.getData('text/plain');
        if (!pastedText) return;

        fragment = document.createDocumentFragment();
        const lines = pastedText.split(/\r\n|\r|\n/);
        lines.forEach((line, index) => {
          if (index > 0) {
            fragment.appendChild(document.createElement('br'));
          }
          fragment.appendChild(document.createTextNode(line));
        });
      }

      range.deleteContents();
      range.insertNode(fragment);

      if (inputRef.current) {
        const newValue = (inputRef.current as HTMLDivElement).innerHTML;
        isInternalChangeRef.current = true;
        onChange(newValue);
      }
    },
    [allowedTags, allowedAttributes, onChange]
  );

  const handleFormatText = useCallback(
    (
      command:
        | 'bold'
        | 'italic'
        | 'underline'
        | 'insertUnorderedList'
        | 'insertOrderedList'
        | 'justifyLeft'
        | 'justifyCenter'
        | 'justifyRight'
    ) => {
      formatText(command, inputRef, onChange, isInternalChangeRef);
    },
    [onChange]
  );

  const handleInsertLink = useCallback(() => {
    insertLink(inputRef, onChange, isInternalChangeRef);
  }, [onChange]);

  const handleInsertImage = useCallback(() => {
    if (!allowedTags.includes('img') || !inputRef.current || !imageInputRef.current) return;

    imageInsertRangeRef.current = getSelectionRangeInEditor(inputRef)?.cloneRange() ?? null;
    imageInputRef.current.click();
  }, [allowedTags]);

  const handleImageFilesSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const imageFiles = Array.from(e.currentTarget.files ?? []).filter(file => file.type.startsWith('image/'));
      e.currentTarget.value = '';
      if (imageFiles.length === 0 || !inputRef.current) return;

      inputRef.current.focus();
      const savedRange = imageInsertRangeRef.current;
      imageInsertRangeRef.current = null;
      if (savedRange && inputRef.current.contains(savedRange.commonAncestorContainer)) {
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(savedRange);
        }
      }

      isInternalChangeRef.current = true;
      setErrorMessage(
        insertImageFilesAtSelection(imageFiles, inputRef, newValue => {
          isInternalChangeRef.current = true;
          onChange(newValue);
        })
      );
    },
    [onChange]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      const draggedImage = draggedImageRef.current;
      if (draggedImage && inputRef.current?.contains(draggedImage)) {
        draggedImageRef.current = null;
        const dropRange = getDropRange(e.clientX, e.clientY, inputRef.current);
        if (!dropRange || draggedImage.contains(dropRange.commonAncestorContainer)) return;

        const marker = document.createTextNode('');
        dropRange.insertNode(marker);
        marker.parentNode?.insertBefore(draggedImage, marker);
        marker.remove();

        const selectionRange = document.createRange();
        selectionRange.setStartAfter(draggedImage);
        selectionRange.collapse(true);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(selectionRange);

        isInternalChangeRef.current = true;
        onChange(inputRef.current.innerHTML);
        return;
      }

      const files = Array.from(e.dataTransfer.files);
      const imageFiles = files.filter(file => file.type.startsWith('image/'));

      if (imageFiles.length === 0) return;
      if (!inputRef.current) return;

      if (!allowedTags.includes('img')) {
        setErrorMessage('Images are not allowed.');
        return;
      }

      inputRef.current.focus();
      const dropRange = getDropRange(e.clientX, e.clientY, inputRef.current);
      if (dropRange) {
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(dropRange);
      }
      isInternalChangeRef.current = true;
      setErrorMessage(
        insertImageFilesAtSelection(imageFiles, inputRef, newValue => {
          isInternalChangeRef.current = true;
          onChange(newValue);
        })
      );
    },
    [allowedTags, onChange]
  );

  const handleDragStart = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (!(e.target instanceof HTMLImageElement) || !inputRef.current?.contains(e.target)) return;

    draggedImageRef.current = e.target;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', 'rich-text-editor-image');
  }, []);

  const handleDragEnd = useCallback(() => {
    draggedImageRef.current = null;
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = draggedImageRef.current ? 'move' : 'copy';

    const isImageDrag = Boolean(draggedImageRef.current) || Array.from(e.dataTransfer.types).includes('Files');
    if (!isImageDrag || !inputRef.current) return;

    const dropRange = getDropRange(e.clientX, e.clientY, inputRef.current);
    if (!dropRange || draggedImageRef.current?.contains(dropRange.commonAncestorContainer)) return;

    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(dropRange);
  }, []);

  const handleCopy = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    const range = getSelectionRangeInEditor(inputRef);
    if (!range) return;

    const fragment = range.cloneContents();
    const wrapper = document.createElement('div');
    wrapper.appendChild(fragment);

    const html = wrapper.innerHTML;
    const plainText = (fragment.textContent ?? '').trim();

    const imgElements = wrapper.querySelectorAll('img');
    if (imgElements.length > 0) {
      e.preventDefault();
      e.clipboardData.setData('text/html', html);
      e.clipboardData.setData('text/plain', plainText);

      for (const img of Array.from(imgElements)) {
        const src = img.getAttribute('src');
        if (!src) continue;
        const file = dataUrlToFile(src);
        if (file) e.clipboardData.items.add(file);
      }
    }
  }, []);

  return {
    inputRef,
    imageInputRef,
    hasRichTextTags,
    errorMessage,
    handleInput,
    handlePaste,
    handleFormatText,
    handleInsertLink,
    handleInsertImage,
    handleImageFilesSelected,
    handleDrop,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleCopy,
  };
};
